import { useState, useCallback } from 'react';
import { FieldData } from '@/types/label';

const MAX_FIELD_HISTORY = 30;

interface FieldHistoryState {
  fieldData: FieldData;
  timestamp: number;
}

export const useFieldHistory = (initialFieldData: FieldData) => {
  const [history, setHistory] = useState<FieldHistoryState[]>([
    { fieldData: { ...initialFieldData }, timestamp: Date.now() },
  ]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const canUndo = currentIndex > 0;
  const canRedo = currentIndex < history.length - 1;

  const pushFieldState = useCallback((fieldData: FieldData) => {
    setHistory((prev) => {
      const newHistory = prev.slice(0, currentIndex + 1);
      newHistory.push({
        fieldData: { ...fieldData },
        timestamp: Date.now(),
      });
      if (newHistory.length > MAX_FIELD_HISTORY) {
        newHistory.shift();
        setCurrentIndex(newHistory.length - 1);
        return newHistory;
      }
      setCurrentIndex(newHistory.length - 1);
      return newHistory;
    });
  }, [currentIndex]);

  const undo = useCallback((): FieldData | null => {
    if (!canUndo) return null;
    const newIndex = currentIndex - 1;
    setCurrentIndex(newIndex);
    return { ...history[newIndex].fieldData };
  }, [canUndo, currentIndex, history]);

  const redo = useCallback((): FieldData | null => {
    if (!canRedo) return null;
    const newIndex = currentIndex + 1;
    setCurrentIndex(newIndex);
    return { ...history[newIndex].fieldData };
  }, [canRedo, currentIndex, history]);

  const clearFieldHistory = useCallback((fieldData: FieldData) => {
    setHistory([{ fieldData: { ...fieldData }, timestamp: Date.now() }]);
    setCurrentIndex(0);
  }, []);

  return {
    pushFieldState,
    undoField: undo,
    redoField: redo,
    canUndoField: canUndo,
    canRedoField: canRedo,
    clearFieldHistory,
  };
};
